/**
 * Utility functions for the Onboarding Ramp tab.
 *
 * mwValid / mwDefault / mwProblem mirror the v82 mockup logic exactly so that
 * validation and defaults are consistent with the design reference.
 *
 * No ramp values are generated here: every target shown on the page comes from the backend
 * (GET /people/admin/configuration/ramp) and is held in the `peopleConfig.ramp` Redux cache.
 */

import type { ApiRampConfig } from '../../../api/peopleConfigApi';
import { LocationOnboardingConfig, METRICS_CONFIG } from './types';

// ─── API mapping ──────────────────────────────────────────────────────────────

/**
 * Maps a backend RampConfigDTO to the local `LocationOnboardingConfig`.
 * Returns `null` when the response carries no ramp (no timeline or no targets), so the caller can
 * show an empty state instead of inventing values.
 *
 * `appliesTo` is `'national'` or the location name the request was made for.
 */
export function mapApiRampToConfig(
  data: ApiRampConfig | null | undefined,
  appliesTo = 'national'
): LocationOnboardingConfig | null {
  if (!data || !data.onboardingWeeks || !data.targets || Object.keys(data.targets).length === 0) {
    return null;
  }

  const weeks = Math.min(26, Math.max(4, Number(data.onboardingWeeks)));
  const apiMw = Array.isArray(data.milestoneWeeks) ? data.milestoneWeeks.map(Number) : [];
  const milestoneWeeks: [number, number, number] =
    apiMw.length >= 3 && mwValid(apiMw, weeks)
      ? [apiMw[0], apiMw[1], apiMw[2]]
      : mwDefault(weeks);

  const iw = data.itemWeeks;
  const targets: Record<string, number[]> = {};
  METRICS_CONFIG.forEach(({ key }) => {
    const src = Array.isArray(data.targets[key]) ? data.targets[key].map(Number) : [];
    targets[key] = Array.from({ length: weeks }, (_, i) => src[i] ?? src[src.length - 1] ?? 0);
  });

  return {
    location: appliesTo,
    scopeType: data.scopeType,
    onboardingWeeks: weeks,
    milestoneWeeks,
    itemWeeks: {
      preferred: Number(iw?.preferred) || 2,
      training: Number(iw?.training) || 1,
      certification: Number(iw?.certification) || 4,
    },
    targets,
    updatedBy: data.updatedBy,
    updatedOn: data.updatedOn,
  };
}

// ─── Milestone week helpers (mirrors v82 mwDefault / mwValid / mwProblem) ─────

/** Default milestone weeks for a given timeline length: ~⅓, ~⅔, last. */
export function mwDefault(weeks: number): [number, number, number] {
  const m1 = Math.max(1, Math.round(weeks * 0.33));
  const m2 = Math.max(m1 + 1, Math.round(weeks * 0.67));
  const m3 = Math.max(m2 + 1, weeks);
  return [m1, m2, m3];
}

/** True when the three milestone weeks are strictly ascending and all ≤ onboardingWeeks. */
export function mwValid(mw: number[], onboardingWeeks: number): boolean {
  if (!Array.isArray(mw) || mw.length < 3) return false;
  const [m1, m2, m3] = mw;
  return (
    Number.isFinite(m1) &&
    Number.isFinite(m2) &&
    Number.isFinite(m3) &&
    m1 >= 1 &&
    m1 < m2 &&
    m2 < m3 &&
    m3 <= onboardingWeeks
  );
}

/** Human-readable error message when milestones are out of order; null when valid. */
export function mwProblem(mw: number[], onboardingWeeks: number): string | null {
  if (!Array.isArray(mw) || mw.length < 3) return 'Enter a week number for each milestone.';
  const [m1, m2, m3] = mw;
  if (!Number.isFinite(m1) || !Number.isFinite(m2) || !Number.isFinite(m3)) {
    return 'Enter a week number for each milestone.';
  }
  if (m1 < 1) return 'Milestone 1 must be week 1 or later.';
  if (m1 >= m2) return 'Milestone 2 must come after Milestone 1.';
  if (m2 >= m3) return 'Milestone 3 must come after Milestone 2.';
  if (m3 > onboardingWeeks) return `Milestone 3 cannot exceed the ${onboardingWeeks}-week timeline.`;
  return null;
}

// ─── Display helpers ──────────────────────────────────────────────────────────

/**
 * One-line summary shown in the "Published configuration" sub-card on Card 1.
 * Matches the v82 mockup: "12 weeks / milestones at weeks 4, 8, 12 / PM 2, training 1, certification 4"
 */
export function formatPublishedSummary(cfg: LocationOnboardingConfig): string {
  const mw = cfg.milestoneWeeks;
  const iw = cfg.itemWeeks;
  return (
    `${cfg.onboardingWeeks} weeks / milestones at weeks ${mw.join(', ')} / ` +
    `PM ${iw.preferred}, training ${iw.training}, certification ${iw.certification}`
  );
}
